import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import {
  getProjectCohortPath,
  getProjectCohortVersionPath,
  getProjectPath,
} from '@verantos-dev/core-constants';
import { useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';
import type { EpiResourceRef } from '@verantos-dev/feature-epi-chat';

import { useActiveChatContext } from '../context/ActiveChatContext';

const RESOLVE_TIMEOUT_MS = 5000;

/**
 * Scan the active chat for a cohort ``resource_ref`` keyed by the URL's
 * ``cohortId`` segment. ``draft_cohort`` emits a card with the
 * ``client_id`` in the route while the sub-agent is composing — this
 * arm matches that pre-commit window so the resolver can render a
 * skeleton instead of 404-ing the not-yet-existent cohort. After
 * ``commit_cohort_plan`` lands, the ratchet emit replaces ``status``
 * with ``ready`` AND populates ``resourceId`` with the real cohort id,
 * so we use the same ref later to redirect to the canonical URL.
 */
const findCohortResourceRefForId = (
  ctx: ReturnType<typeof useActiveChatContext>,
  cohortIdOrClientId: string
): EpiResourceRef | null => {
  if (!ctx || !cohortIdOrClientId) return null;
  for (const message of ctx.page.messages) {
    for (const part of message.parts) {
      if (part.kind !== 'resource_ref') continue;
      const ref = part.resourceRef;
      if (ref.resourceKind !== 'cohort') continue;
      if (ref.clientId === cohortIdOrClientId) return ref;
      if (ref.resourceId === cohortIdOrClientId) return ref;
    }
  }
  return null;
};

/**
 * Hosts `/projects/:projectId/cohort/:cohortId` (no version segment) — the
 * shape we expose in the artifact list and breadcrumb. Resolves the
 * latest cohort version and replaces the URL with
 * `/projects/:projectId/cohort/:cohortId/cohort-version/:cohortVersionId`,
 * which is the form `CohortVersionLayout` and the cohort-builder
 * `useParams` callsites already expect.
 *
 * **Pre-manifest skeleton (OR-6089).** When the user clicks the
 * "Drafting…" cohort card in chat, the URL carries the chat card's
 * ``client_id`` — the cohort itself doesn't exist yet, so the REST
 * profile fetch would 404. The resolver instead looks for an
 * in-flight cohort ``resource_ref`` matching the URL segment and
 * renders a "Preparing cohort…" skeleton until the ratchet emit
 * lands. Once the ref ratchets to ``ready`` with a real ``resourceId``,
 * the resolver redirects to the canonical URL.
 *
 * Most navigations come from the artifact list, which already has the
 * latest version id and skips this resolver entirely. This page fires
 * for typed URLs, external deep links, and chat-card clicks during
 * the drafting window.
 */
export const CohortVersionResolverPage = () => {
  const { projectId = '', cohortId = '' } = useParams<{ projectId: string; cohortId: string }>();
  // Preserve ``?chat=`` across the redirect so a user landing here
  // from a chat-open URL doesn't lose their chat sidecar when the
  // resolver replaces the path with the versioned form.
  const [searchParams] = useSearchParams();
  const ctx = useActiveChatContext();

  // Match the URL's cohortId against any cohort ``resource_ref``
  // (by clientId or resourceId). Pre-manifest URLs hit on clientId;
  // post-ratchet URLs hit on resourceId.
  const cohortRef = findCohortResourceRefForId(ctx, cohortId);
  const isPreManifestDraft =
    cohortRef !== null &&
    cohortRef.clientId === cohortId &&
    cohortRef.status !== 'ready' &&
    cohortRef.status !== 'error';
  // Post-ratchet: URL still on the clientId, but the ref now has the
  // real cohort id. Redirect so breadcrumbs + downstream fetches see
  // the canonical form.
  const ratchetedCohortId =
    cohortRef !== null &&
    cohortRef.status === 'ready' &&
    cohortRef.resourceId &&
    cohortRef.resourceId !== cohortId &&
    cohortRef.clientId === cohortId
      ? cohortRef.resourceId
      : null;

  // Skip the REST profile fetch during the pre-manifest drafting
  // window — the cohort doesn't exist yet and the call would 404.
  // Same posture once we've ratcheted and are about to redirect.
  const { data, error, isLoading } = useCohortProfileQuery({
    projectId,
    cohortId,
    enabled: !isPreManifestDraft && !ratchetedCohortId,
  });

  const latestCohortVersionId = data?.latestCohortVersionId;

  // Match `CohortBuilderLoadingPage` — bail back to the project root if the
  // cohort fetch hangs past the timeout, so users aren't stuck on a spinner.
  useEffect(() => {
    if (latestCohortVersionId) return;
    const timeout = setTimeout(() => {
      // Soft fallback only — the parent <Navigate> below handles the
      // happy path; this exists to surface the error path.
    }, RESOLVE_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [latestCohortVersionId]);

  if (ratchetedCohortId) {
    const chat = searchParams.get('chat');
    return (
      <Navigate
        replace
        to={{
          pathname: getProjectCohortPath(projectId, ratchetedCohortId),
          search: chat ? `?chat=${chat}` : '',
        }}
      />
    );
  }

  if (isPreManifestDraft) {
    return (
      <Box
        data-testid="project-cohort-resolver-draft-skeleton"
        sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Preparing cohort…
        </Typography>
        <Skeleton variant="text" width={280} height={32} />
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (latestCohortVersionId) {
    const chat = searchParams.get('chat');
    return (
      <Navigate
        replace
        to={{
          pathname: getProjectCohortVersionPath(projectId, cohortId, latestCohortVersionId),
          search: chat ? `?chat=${chat}` : '',
        }}
      />
    );
  }

  if (error && !isLoading) {
    return (
      <Box
        data-testid="project-cohort-resolver-error"
        sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Typography variant="subtitle1" color="error">
          Could not load cohort.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The cohort could not be resolved. <a href={getProjectPath(projectId)}>Back to project</a>.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="project-cohort-resolver-loading"
      sx={{ display: 'flex', justifyContent: 'center', paddingY: 6 }}
    >
      <CircularProgress size={24} />
    </Box>
  );
};
